/**
 * FASE 24.7 — HUMAN RHYTHM DETECTION
 *
 * Detects usage frequency, session intensity, and operational fatigue.
 * Kevia adapts subtly — never mentions fatigue to the user explicitly.
 *
 * Used to:
 *  - Reduce proactive hints during saturated sessions
 *  - Recognize low-activity periods (less noise)
 *  - Guide render priority resolver toward calmer output
 */

import { getTimeline } from "./sessionTimeline";
import { RT_EVENTS }   from "./runtimeEvents";

const MS_PER_HOUR   = 60 * 60 * 1000;
const MS_PER_MINUTE = 60 * 1000;

/**
 * @typedef {Object} SessionRhythm
 * @property {"low"|"medium"|"high"} intensity
 * @property {number} fatigueScore  — 0-1 (1 = highly saturated)
 * @property {boolean} shouldReduceHints
 * @property {number}  sessionDurationMs
 * @property {number}  actionsPerHour
 * @property {number}  messageCount
 */

/**
 * Analyze the current session rhythm from the runtime timeline.
 * @param {object[]} [timelineOverride] — for testing; defaults to getTimeline()
 * @returns {SessionRhythm}
 */
export function detectSessionRhythm(timelineOverride = null) {
  const timeline = timelineOverride ?? getTimeline();
  if (!timeline.length) {
    return {
      intensity:          "low",
      fatigueScore:       0,
      shouldReduceHints:  false,
      sessionDurationMs:  0,
      actionsPerHour:     0,
      messageCount:       0,
    };
  }

  const now         = Date.now();
  const sessionStart = timeline[0]?.ts ?? now;
  const sessionDurationMs = now - sessionStart;

  // Count actions in the last hour
  const lastHourEvents = timeline.filter((ev) => now - ev.ts < MS_PER_HOUR);
  const actionsPerHour = sessionDurationMs > MS_PER_HOUR
    ? (timeline.length / (sessionDurationMs / MS_PER_HOUR))
    : lastHourEvents.length;

  // Count message events
  const messageCount = timeline.filter((ev) => ev.type === RT_EVENTS.SEND_MESSAGE).length;

  // Count navigation bursts (rapid module switches)
  const navEvents       = timeline.filter((ev) => ev.type === RT_EVENTS.NAVIGATE || ev.type === RT_EVENTS.OPEN_MODULE);
  const recentNavEvents = navEvents.filter((ev) => now - ev.ts < 10 * MS_PER_MINUTE);
  const navBurstScore   = Math.min(1, recentNavEvents.length / 8); // saturates at 8 navigations/10min

  // Session length score
  const sessionHours    = sessionDurationMs / MS_PER_HOUR;
  const lengthScore     = Math.min(1, sessionHours / 3); // saturates at 3 hours

  // Message density score
  const msgDensity      = sessionDurationMs > 0
    ? Math.min(1, messageCount / Math.max(1, sessionDurationMs / MS_PER_HOUR) / 15) // 15 msgs/hr = max
    : 0;

  // Combined fatigue score
  const fatigueScore = Math.min(1,
    navBurstScore * 0.4 +
    lengthScore   * 0.3 +
    msgDensity    * 0.3,
  );

  let intensity;
  if      (fatigueScore >= 0.65 || actionsPerHour > 40) intensity = "high";
  else if (fatigueScore >= 0.30 || actionsPerHour > 15) intensity = "medium";
  else                                                   intensity = "low";

  const shouldReduceHints = fatigueScore >= 0.65;

  return {
    intensity,
    fatigueScore:      Math.round(fatigueScore * 100) / 100,
    shouldReduceHints,
    sessionDurationMs,
    actionsPerHour:    Math.round(actionsPerHour),
    messageCount,
  };
}

/**
 * Returns true when the session is in a high-fatigue state.
 * Use to suppress non-urgent proactive hints.
 */
export function isSessionSaturated() {
  return detectSessionRhythm().fatigueScore >= 0.65;
}
